import { fmt, fmtShort } from '@/lib/utils';
import type { LCCAResults, LCCAParameters } from '@lcca-nexus/shared';
import { TrendingDown, DollarSign, Calendar, BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface KPICardsProps {
  results: LCCAResults;
  params: LCCAParameters;
}

export function KPICards({ results, params }: KPICardsProps) {
  const costPerYear = results.totalLCC / params.analysisPeriod;

  const cards = [
    {
      label: 'Total Life-Cycle Cost',
      value: fmtShort(results.totalLCC),
      sub: `Net Present Value over ${params.analysisPeriod} years`,
      icon: DollarSign,
      accent: 'primary',
    },
    {
      label: 'Uniform Annual Cost',
      value: fmtShort(results.uac),
      sub: 'Levelised equivalent per year',
      icon: TrendingDown,
      accent: 'gold',
    },
    {
      label: 'Residual Value (PW)',
      value: fmtShort(results.residualPW),
      sub: `Salvage at Year ${params.analysisPeriod}`,
      icon: BarChart3,
      accent: 'teal',
    },
    {
      label: 'Average Annual Cost',
      value: fmtShort(costPerYear),
      sub: 'Simple average (undiscounted)',
      icon: Calendar,
      accent: 'neutral',
    },
  ];

  const accentStyles = {
    primary: 'from-navy-700 to-navy-500',
    gold:    'from-amber-600 to-amber-400',
    teal:    'from-teal-700 to-teal-500',
    neutral: 'from-parchment-300 to-parchment-200',
  };

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
      {cards.map((card, i) => {
        const Icon = card.icon;
        return (
          <div
            key={card.label}
            className={cn('kpi-card animate-fade-up')}
            style={{ animationDelay: `${i * 60}ms` }}
          >
            {/* Top accent bar */}
            <div className={cn('absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r', accentStyles[card.accent as keyof typeof accentStyles])} />

            <div className="flex items-start justify-between mb-3">
              <p className="text-xs font-display font-semibold uppercase tracking-widest text-navy-400">
                {card.label}
              </p>
              <div className={cn(
                'w-7 h-7 rounded-lg flex items-center justify-center',
                card.accent === 'primary' ? 'bg-navy-50 text-navy-600' :
                card.accent === 'gold'    ? 'bg-amber-50 text-amber-600' :
                card.accent === 'teal'    ? 'bg-teal-50 text-teal-600' :
                'bg-parchment-100 text-navy-400'
              )}>
                <Icon size={14} />
              </div>
            </div>

            <div className="font-mono font-semibold text-navy-900 text-2xl leading-none mb-1.5 animate-count-up">
              {card.value}
            </div>

            <p className="text-xs text-navy-400 leading-snug">{card.sub}</p>
          </div>
        );
      })}
    </div>
  );
}
